import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout.jsx';
import ApiClient from '../../utils/ApiClient.js';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import { getFirstAttributeValue } from '../../utils/KeycloakAttributeUtils.js';
import { USER_TYPE_OPTIONS, normalizeUserTypeCode } from '../../utils/UserTypeUtils.js';

const parseBooleanPreference = (value, fallback = false) => {
    if (typeof value === 'boolean') {
        return value;
    }

    const normalizedValue = String(value ?? '').trim().toLowerCase();
    if (normalizedValue === 'true') {
        return true;
    }
    if (normalizedValue === 'false') {
        return false;
    }

    return fallback;
};

const getTokenAttributeValue = (tokenDetails, keys, fallback = '') => {
    const directClaimValue = getFirstAttributeValue(tokenDetails, keys, '');
    if (directClaimValue !== undefined && directClaimValue !== null && directClaimValue !== '') {
        return directClaimValue;
    }
    return getFirstAttributeValue(tokenDetails?.attributes, keys, fallback);
};

const normalizeDateForInput = (value) => {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
        return '';
    }

    const datePrefixMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (datePrefixMatch) {
        return datePrefixMatch[1];
    }

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return '';
    }

    return parsedDate.toISOString().slice(0, 10);
};

const EditProfilePage = ({ authDetails, onSignOut }) => {
    const navigate = useNavigate();
    const tokenDetails = authDetails?.identity?.tokenParsed ?? {};
    const subject = tokenDetails.sub ?? authDetails?.identity?.subject ?? null;
    const tokenPreferredUsername = tokenDetails.preferred_username ?? null;
    const tokenEmail = tokenDetails.email ?? null;
    const tokenSlackNotifications = getTokenAttributeValue(tokenDetails, ['slackNotifications'], '');
    const tokenEmailNotifications = getTokenAttributeValue(tokenDetails, ['emailNotifications'], '');
    const tokenJobTitle = getTokenAttributeValue(tokenDetails, ['jobTitle', 'JobTitle'], '');
    const tokenUserType = normalizeUserTypeCode(
        getTokenAttributeValue(tokenDetails, ['UserType', 'userType'], '')
    );
    const tokenContractNumber = getTokenAttributeValue(
        tokenDetails,
        ['contractNumber', 'ContractNumber'],
        ''
    );
    const tokenContractExpirationDate = normalizeDateForInput(
        getTokenAttributeValue(tokenDetails, ['contractExpirationDate', 'ContractExpirationDate'], '')
    );
    const [supervisors, setSupervisors] = useState([]);
    const [isLoadingSupervisors, setIsLoadingSupervisors] = useState(true);
    const [supervisorsError, setSupervisorsError] = useState('');
    const [formValues, setFormValues] = useState({
        firstName: tokenDetails.given_name || '',
        lastName: tokenDetails.family_name || '',
        email: tokenDetails.email || '',
        supervisor: tokenDetails.supervisor || '',
        slackNotifications: parseBooleanPreference(tokenSlackNotifications, false),
        emailNotifications: parseBooleanPreference(tokenEmailNotifications, false),
        jobTitle: tokenJobTitle || '',
        userType: tokenUserType || '',
        contractNumber: tokenContractNumber || '',
        contractExpirationDate: tokenContractExpirationDate || '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const isContractor = normalizeUserTypeCode(formValues.userType) === 'CTR';

    useEffect(() => {
        let isMounted = true;

        const fetchSupervisors = async () => {
            setIsLoadingSupervisors(true);
            setSupervisorsError('');

            try {
                const response = await ApiClient.get('/users');

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    const responseUsers = response.data?.content ?? response.data?.users ?? response.data ?? [];
                    const usersList = Array.isArray(responseUsers) ? responseUsers : [];
                    const currentUserIds = new Set([
                        subject,
                        tokenPreferredUsername,
                        tokenEmail,
                    ].filter(Boolean));
                    const filteredUsers = usersList.filter((user) => {
                        const candidateIds = [
                            user?.id,
                            user?.uuid,
                            user?.userId,
                            user?.username,
                            user?.preferred_username,
                            user?.email,
                        ].filter(Boolean);
                        return !candidateIds.some((id) => currentUserIds.has(id));
                    });
                    setSupervisors(filteredUsers);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorsError('We could not load the supervisor list.');
                    setSupervisors([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingSupervisors(false);
                }
            }
        };

        fetchSupervisors();

        return () => {
            isMounted = false;
        };
    }, [subject, tokenEmail, tokenPreferredUsername]);

    useEffect(() => {
        let isMounted = true;

        if (!subject) {
            return () => {
                isMounted = false;
            };
        }

        const fetchProfilePreferences = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(subject)}`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    const profileAttributes =
                        response.data?.attributes && typeof response.data.attributes === 'object'
                            ? response.data.attributes
                            : {};
                    const supervisor = getFirstAttributeValue(
                        profileAttributes,
                        ['supervisor', 'Supervisor'],
                        ''
                    );
                    const slackNotifications = parseBooleanPreference(
                        getFirstAttributeValue(profileAttributes, ['slackNotifications'], tokenSlackNotifications),
                        false
                    );
                    const emailNotifications = parseBooleanPreference(
                        getFirstAttributeValue(profileAttributes, ['emailNotifications'], tokenEmailNotifications),
                        false
                    );
                    const jobTitle = getFirstAttributeValue(
                        profileAttributes,
                        ['jobTitle', 'JobTitle'],
                        tokenJobTitle
                    );
                    const userType = normalizeUserTypeCode(
                        getFirstAttributeValue(
                            profileAttributes,
                            ['UserType', 'userType'],
                            response.data?.UserType ?? response.data?.userType ?? tokenUserType
                        )
                    );
                    const contractNumber = getFirstAttributeValue(
                        profileAttributes,
                        ['contractNumber', 'ContractNumber'],
                        tokenContractNumber
                    );
                    const contractExpirationDate = normalizeDateForInput(
                        getFirstAttributeValue(
                            profileAttributes,
                            ['contractExpirationDate', 'ContractExpirationDate'],
                            tokenContractExpirationDate
                        )
                    );

                    setFormValues((prev) => ({
                        ...prev,
                        supervisor: supervisor || prev.supervisor,
                        slackNotifications,
                        emailNotifications,
                        jobTitle: jobTitle || prev.jobTitle,
                        userType: userType || prev.userType,
                        contractNumber: contractNumber || prev.contractNumber,
                        contractExpirationDate: contractExpirationDate || prev.contractExpirationDate,
                    }));
                }
            } catch (error) {
                // Keep token-derived defaults when profile lookup fails.
            }
        };

        fetchProfilePreferences();

        return () => {
            isMounted = false;
        };
    }, [
        subject,
        tokenContractExpirationDate,
        tokenContractNumber,
        tokenEmailNotifications,
        tokenJobTitle,
        tokenSlackNotifications,
        tokenUserType,
    ]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        if (name === 'userType') {
            const normalizedUserType = normalizeUserTypeCode(value);
            setFormValues((prev) => ({
                ...prev,
                userType: normalizedUserType,
                ...(normalizedUserType === 'CTR'
                    ? {}
                    : {
                        contractNumber: '',
                        contractExpirationDate: '',
                    }),
            }));
            return;
        }

        setFormValues((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitError('');
        setSubmitSuccess(false);

        if (!subject) {
            setSubmitError('Your profile identifier is missing. Please refresh and try again.');
            return;
        }

        const normalizedUserType = normalizeUserTypeCode(formValues.userType);
        if (!normalizedUserType) {
            setSubmitError('User type is required before you can save your profile.');
            return;
        }

        if (
            normalizedUserType === 'CTR' &&
            (!String(formValues.contractNumber || '').trim() ||
                !String(formValues.contractExpirationDate || '').trim())
        ) {
            setSubmitError('Contract number and contract expiration date are required for contractors.');
            return;
        }

        const payload = {
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            email: formValues.email,
            supervisor: formValues.supervisor,
            slackNotifications: formValues.slackNotifications,
            emailNotifications: formValues.emailNotifications,
            jobTitle: String(formValues.jobTitle || '').trim(),
            UserType: normalizedUserType,
            contractNumber:
                normalizedUserType === 'CTR' ? String(formValues.contractNumber || '').trim() : '',
            contractExpirationDate:
                normalizedUserType === 'CTR' ? String(formValues.contractExpirationDate || '').trim() : '',
        };

        setIsSubmitting(true);

        try {
            await ApiClient.post(`/users/${encodeURIComponent(subject)}/profile`, payload);
            setSubmitSuccess(true);
        } catch (error) {
            setSubmitError('We could not update your profile. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-8">
                            <div className="edit-profile-page__header">
                                <h1 className="usa-heading edit-profile-page__title">Edit profile</h1>
                                <Link className="usa-link" to="/profile">
                                    Return to profile
                                </Link>
                            </div>
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__body">
                                        {supervisorsError ? (
                                            <div className="usa-alert usa-alert--warning" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{supervisorsError}</p>
                                                </div>
                                            </div>
                                        ) : null}
                                        {submitError ? (
                                            <div className="usa-alert usa-alert--error" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{submitError}</p>
                                                </div>
                                            </div>
                                        ) : null}
                                        {submitSuccess ? (
                                            <div className="usa-alert usa-alert--success" role="status">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">
                                                        Your profile updates were submitted successfully.
                                                    </p>
                                                </div>
                                            </div>
                                        ) : null}
                                        <form className="usa-form profile-form" onSubmit={handleSubmit}>
                                            <div className="profile-form__grid">
                                                <label className="usa-label" htmlFor="profile-first-name">
                                                    First name
                                                </label>
                                                <input
                                                    className="usa-input"
                                                    id="profile-first-name"
                                                    name="firstName"
                                                    type="text"
                                                    value={formValues.firstName}
                                                    onChange={handleChange}
                                                />

                                                <label className="usa-label" htmlFor="profile-last-name">
                                                    Last name
                                                </label>
                                                <input
                                                    className="usa-input"
                                                    id="profile-last-name"
                                                    name="lastName"
                                                    type="text"
                                                    value={formValues.lastName}
                                                    onChange={handleChange}
                                                />

                                                <label className="usa-label" htmlFor="profile-email">
                                                    Email
                                                </label>
                                                <input
                                                    className="usa-input"
                                                    id="profile-email"
                                                    name="email"
                                                    type="email"
                                                    value={formValues.email}
                                                    onChange={handleChange}
                                                />

                                                <label className="usa-label" htmlFor="profile-job-title">
                                                    Job title
                                                </label>
                                                <input
                                                    className="usa-input"
                                                    id="profile-job-title"
                                                    name="jobTitle"
                                                    type="text"
                                                    value={formValues.jobTitle}
                                                    onChange={handleChange}
                                                />

                                                <label className="usa-label usa-label--required" htmlFor="profile-user-type">
                                                    User type
                                                </label>
                                                <select
                                                    className="usa-select"
                                                    id="profile-user-type"
                                                    name="userType"
                                                    value={formValues.userType}
                                                    onChange={handleChange}
                                                    aria-required="true"
                                                    disabled={isSubmitting}
                                                >
                                                    <option value="">Select a user type</option>
                                                    {USER_TYPE_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>

                                                {isContractor ? (
                                                    <>
                                                        <label className="usa-label usa-label--required" htmlFor="profile-contract-number">
                                                            Contract number
                                                        </label>
                                                        <input
                                                            className="usa-input"
                                                            id="profile-contract-number"
                                                            name="contractNumber"
                                                            type="text"
                                                            value={formValues.contractNumber}
                                                            onChange={handleChange}
                                                            aria-required="true"
                                                            disabled={isSubmitting}
                                                        />

                                                        <label className="usa-label usa-label--required" htmlFor="profile-contract-expiration-date">
                                                            Contract expiration date
                                                        </label>
                                                        <div className="usa-date-picker profile-form__date-picker">
                                                            <input
                                                                className="usa-input"
                                                                id="profile-contract-expiration-date"
                                                                name="contractExpirationDate"
                                                                type="date"
                                                                value={formValues.contractExpirationDate}
                                                                onChange={handleChange}
                                                                aria-required="true"
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </>
                                                ) : null}

                                                <label className="usa-label" htmlFor="profile-supervisor">
                                                    Supervisor
                                                </label>
                                                {isLoadingSupervisors ? (
                                                    <div aria-hidden="true">
                                                        <ValueWithSkeleton loading type="sentence" width="18ch" />
                                                    </div>
                                                ) : (
                                                    <select
                                                        className="usa-select"
                                                        id="profile-supervisor"
                                                        name="supervisor"
                                                        value={formValues.supervisor}
                                                        onChange={handleChange}
                                                        disabled={isLoadingSupervisors}
                                                    >
                                                        <option value="">Select a supervisor</option>
                                                        {supervisors.map((supervisor) => {
                                                            const supervisorId = supervisor.id ?? supervisor.uuid ?? supervisor.userId ?? supervisor.username;
                                                            const supervisorName =
                                                                supervisor.name ||
                                                                `${supervisor.firstName || ''} ${supervisor.lastName || ''}`.trim() ||
                                                                supervisor.preferred_username ||
                                                                supervisor.username ||
                                                                supervisor.email ||
                                                                supervisorId ||
                                                                'Supervisor';
                                                            if (!supervisorId) {
                                                                return null;
                                                            }
                                                            return (
                                                                <option key={supervisorId} value={supervisorId}>
                                                                    {supervisorName}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                )}

                                                <span className="usa-label">Slack notifications</span>
                                                <div className="usa-toggle">
                                                    <input
                                                        id="profile-slack-notifications"
                                                        className="usa-toggle__input"
                                                        name="slackNotifications"
                                                        type="checkbox"
                                                        checked={formValues.slackNotifications}
                                                        onChange={handleChange}
                                                        disabled={isSubmitting}
                                                        aria-label={`Slack notifications: ${formValues.slackNotifications ? 'On' : 'Off'}`}
                                                    />
                                                    <label
                                                        className="usa-toggle__label"
                                                        htmlFor="profile-slack-notifications"
                                                    >
                                                        <span className="profile-form__toggle-state">
                                                            {formValues.slackNotifications ? 'On' : 'Off'}
                                                        </span>
                                                    </label>
                                                </div>

                                                <span className="usa-label">Email notifications</span>
                                                <div className="usa-toggle">
                                                    <input
                                                        id="profile-email-notifications"
                                                        className="usa-toggle__input"
                                                        name="emailNotifications"
                                                        type="checkbox"
                                                        checked={formValues.emailNotifications}
                                                        onChange={handleChange}
                                                        disabled={isSubmitting}
                                                        aria-label={`Email notifications: ${formValues.emailNotifications ? 'On' : 'Off'}`}
                                                    />
                                                    <label
                                                        className="usa-toggle__label"
                                                        htmlFor="profile-email-notifications"
                                                    >
                                                        <span className="profile-form__toggle-state">
                                                            {formValues.emailNotifications ? 'On' : 'Off'}
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="usa-button-group profile-form__actions">
                                                <button
                                                    type="button"
                                                    className="usa-button usa-button--outline"
                                                    onClick={() => navigate('/profile')}
                                                    disabled={isSubmitting}
                                                >
                                                    Cancel
                                                </button>
                                                <button type="submit" className="usa-button" disabled={isSubmitting}>
                                                    {isSubmitting ? 'Saving...' : 'Save changes'}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default EditProfilePage;
